/**
 * Company vehicles: every van at a glance — who is driving it, and whether its papers
 * are in order. A vehicle with lapsed insurance should be visible from this list without
 * opening it, so the documents column is the one that shouts.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Card, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { usePermissions } from '@/features/access/usePermissions';
import { VehicleForm } from '@/features/vehicles/VehicleForm';
import { ExportSheetButton, ImportSheetDialog } from '@/features/sheets/ExcelSheet';
import { useI18n } from '@/i18n';
import { api, type Vehicle } from '@/lib/api';
import { dateShort } from '@/lib/format';
import { useTableState, type SortOption } from '@/lib/table';

import { DocumentsBadge, StatusBadge, vehicleTitle } from '@/features/vehicles/parts';

const SORTS: SortOption<Vehicle>[] = [
  // Problems first: the van that may not be driven is the one to find.
  {
    key: 'documents',
    labelKey: 'vehicles.documents',
    value: (v) => v.documents.expired * 100 + v.documents.missing * 10 + v.documents.expiring,
    defaultDirection: 'desc',
  },
  { key: 'plate', labelKey: 'vehicles.plate', value: (v) => v.plate },
  { key: 'holder', labelKey: 'vehicles.holder', value: (v) => v.holder?.name ?? '' },
  { key: 'km', labelKey: 'vehicles.km', value: (v) => v.odometerKm ?? -1, defaultDirection: 'desc' },
];

export function VehiclesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<'' | Vehicle['status']>('');

  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: api.vehicles });
  const all = vehicles.data?.vehicles ?? [];

  const table = useTableState<Vehicle>({
    rows: status ? all.filter((v) => v.status === status) : all,
    search: (v) => [v.plate, v.name, v.make, v.model, v.vin, v.holder?.name],
    sorts: SORTS,
    defaultSortKey: 'documents',
    storageKey: 'vehicles',
  });

  return (
    <>
      <PageHeader
        title={t('vehicles.title')}
        subtitle={t('vehicles.subtitle')}
        actions={
          <>
            <ExportSheetButton kind="vehicles" />
            {can('vehicles', 'write') ? (
              <>
                <button type="button" className="btn-secondary" onClick={() => setImporting(true)}>
                  {t('sheet.import')}
                </button>
                <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
                  {t('vehicles.add')}
                </button>
              </>
            ) : null}
          </>
        }
      />
      <ImportSheetDialog kind="vehicles" open={importing} onClose={() => setImporting(false)} />

      <VehicleForm
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(v) => navigate(`/vehicles/${v.id}`)}
      />

      <DataToolbar
        state={table}
        searchPlaceholder={t('vehicles.searchPlaceholder')}
        countOne="vehicles.countOne"
        countMany="vehicles.countMany"
        extra={
          <select
            className="input w-auto"
            value={status}
            aria-label={t('common.status')}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="">{t('common.all')}</option>
            {(['available', 'assigned', 'maintenance', 'retired'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`vehicles.status.${s}`)}
              </option>
            ))}
          </select>
        }
      />

      {vehicles.isLoading ? (
        <Spinner />
      ) : vehicles.error ? (
        <ErrorNotice error={vehicles.error} />
      ) : all.length === 0 ? (
        <Card>
          <div className="py-10 text-center space-y-3">
            <p className="text-sm text-muted">{t('vehicles.empty')}</p>
            {can('vehicles', 'write') ? (
              <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
                {t('vehicles.add')}
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <div className="lg:hidden p-2">
            <MobileList<Vehicle>
              rows={table.rows}
              keyOf={(v) => v.id}
              title={(v) => `${v.plate} · ${vehicleTitle(v)}`}
              subtitle={(v) => v.holder?.name ?? t('vehicles.nobody')}
              badges={(v) => (
                <>
                  <StatusBadge status={v.status} />
                  <DocumentsBadge vehicle={v} />
                </>
              )}
              fields={[
                { label: t('vehicles.km'), value: (v) => (v.odometerKm === null ? null : v.odometerKm.toLocaleString('it-IT')) },
                {
                  label: t('vehicles.nextExpiry'),
                  value: (v) => (v.documents.nextExpiry ? dateShort(v.documents.nextExpiry) : null),
                },
              ]}
              action={(v) => (
                <Link to={`/vehicles/${v.id}`} className="btn-primary">
                  {t('common.details')}
                </Link>
              )}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="plate">
                    {t('vehicles.vehicle')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="holder">
                    {t('vehicles.holder')}
                  </SortableTh>
                  <th className="th">{t('common.status')}</th>
                  <SortableTh state={table} sortKey="documents">
                    {t('vehicles.documents')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="km">
                    {t('vehicles.km')}
                  </SortableTh>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {table.rows.map((v) => (
                  <tr
                    key={v.id}
                    className="hover:bg-surface-alt cursor-pointer"
                    onClick={() => navigate(`/vehicles/${v.id}`)}
                  >
                    <td className="td">
                      <span className="font-mono font-semibold tracking-wide">{v.plate}</span>
                      <span className="block text-xs text-muted">{vehicleTitle(v)}</span>
                    </td>
                    <td className="td text-sm">
                      {v.holder ? (
                        <>
                          <span className="font-medium">{v.holder.name}</span>
                          {v.holder.since ? (
                            <span className="block text-xs text-muted">
                              {t('vehicles.since', { date: dateShort(v.holder.since) })}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-subtle">{t('vehicles.nobody')}</span>
                      )}
                    </td>
                    <td className="td">
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="td">
                      <DocumentsBadge vehicle={v} />
                      {v.documents.nextExpiry ? (
                        <span className="block text-xs text-muted mt-0.5">
                          {t('vehicles.nextExpiryOn', { date: dateShort(v.documents.nextExpiry) })}
                        </span>
                      ) : null}
                    </td>
                    <td className="td tabular text-sm text-muted">
                      {v.odometerKm === null ? '—' : `${v.odometerKm.toLocaleString('it-IT')} km`}
                    </td>
                    <td className="td text-end">
                      <Link
                        to={`/vehicles/${v.id}`}
                        className="td-action text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('common.details')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
        </Card>
      )}
    </>
  );
}
